import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import type { Recording, Transcript } from '@jabberr/core';
import { Avatar } from '../components/Avatar.js';
import { Badge, ErrorBox, Spinner } from '../components/ui.js';
import { BOT_IDENTITY, BOT_NAME } from './RoomPanel.js';
import { api, ApiError, errorMessage } from '../lib/api.js';
import { mmss, relativeTime } from '../lib/format.js';
import { useDrawer } from '../state/drawer.js';
import { TranscriptGroups } from '../components/TranscriptGroups.js';

export function RecordingPage() {
  const { recordingId = '' } = useParams();
  const navigate = useNavigate();
  const drawer = useDrawer();
  const [recording, setRecording] = useState<Recording | null>(null);
  // The assistant artwork of the meeting this recording came from.
  const [assistantAvatar, setAssistantAvatar] = useState<string | undefined>(undefined);
  const [transcript, setTranscript] = useState<Transcript | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [draftName, setDraftName] = useState('');
  const [drafting, setDrafting] = useState<string | null>(null);

  /** The review thread for this transcript, if one was started: readouts and requirements drafts
   *  land on it, so they need it to exist. */
  const [reviewId, setReviewId] = useState<string | null>(null);

  /** Sends a document turn (a readout or a requirements draft) on the review thread and opens it;
   *  the draft lands on the Outputs page like every document turn. */
  const sendOnReview = async (key: string, purpose: 'readout' | 'requirements', source: string): Promise<void> => {
    if (!recording || !reviewId) return;
    setDrafting(key);
    setError(null);
    try {
      await api.conversations.send(reviewId, { clientTurnId: crypto.randomUUID(), purpose, source });
      navigate(`/p/${recording.projectId}/c/${reviewId}`);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setDrafting(null);
    }
  };

  const load = useCallback(async () => {
    try {
      const res = await api.recordings.get(recordingId);
      setRecording(res.recording);
      setAssistantAvatar(res.jabberrAssistantImageId ?? res.jabberrAvatarId);
      setTranscript(res.transcript ?? null);
      if (res.transcript) {
        // A review conversation points at the transcript, not the recording.
        const transcriptId = res.transcript.id;
        const { conversations } = await api.conversations.list(res.recording.projectId);
        setReviewId(conversations.find((c) => c.kind === 'review' && c.transcriptId === transcriptId)?.id ?? null);
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) setNotFound(true);
      else setError(errorMessage(err));
    }
  }, [recordingId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (recording) drawer.setInfo({ projectId: recording.projectId, title: recording.title });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recording?.projectId, recording?.title]);

  const inFlight = recording && (recording.status === 'queued' || recording.status === 'transcribing' || recording.status === 'uploaded');
  useEffect(() => {
    if (!inFlight) return;
    const t = setInterval(() => void load(), 5000);
    return () => clearInterval(t);
  }, [inFlight, load]);

  /** Speakers by first appearance, with each one's share of the talking. */
  const speakers = useMemo(() => {
    if (!transcript) return [];
    const seconds = new Map<string, number>();
    for (const s of transcript.segments) seconds.set(s.speaker, (seconds.get(s.speaker) ?? 0) + Math.max(0, s.end - s.start));
    const total = [...seconds.values()].reduce((a, b) => a + b, 0) || 1;
    return [...seconds.entries()].map(([id, secs]) => ({ id, share: Math.round((secs / total) * 100) }));
  }, [transcript]);

  const nameOf = (id: string): string => transcript?.speakerMap[id] ?? id;

  const rename = async (id: string, name: string): Promise<void> => {
    setEditing(null);
    if (!transcript) return;
    const trimmed = name.trim();
    const next = { ...transcript.speakerMap };
    if (trimmed && trimmed !== id) next[id] = trimmed;
    else delete next[id];
    if (JSON.stringify(next) === JSON.stringify(transcript.speakerMap)) return;
    setTranscript({ ...transcript, speakerMap: next });
    try {
      const { transcript: saved } = await api.recordings.updateSpeakers(transcript.id, { speakerMap: next });
      setTranscript(saved);
    } catch (err) {
      setError(errorMessage(err));
      void load();
    }
  };

  const review = async (): Promise<void> => {
    if (!recording) return;
    if (reviewId) {
      navigate(`/p/${recording.projectId}/c/${reviewId}`);
      return;
    }
    setBusy(true);
    try {
      const { conversationId, firstTurn } = await api.recordings.review(recordingId);
      navigate(`/p/${recording.projectId}/c/${conversationId}`, { state: { firstTurn } });
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  };

  if (notFound) {
    return (
      <div className="page">
        <h1>Recording not found</h1>
        <Link to="/">Go home</Link>
      </div>
    );
  }
  if (!recording) {
    return (
      <div className="page">
        {error ? <ErrorBox>{error}</ErrorBox> : <Spinner label="Loading" />}
      </div>
    );
  }

  const isBot = (id: string): boolean => recording.source === 'meeting' && id === BOT_NAME;
  const meta = [
    SOURCE_LABEL[recording.source] ?? recording.source,
    recording.durationS != null ? mmss(recording.durationS) : null,
    relativeTime(recording.createdAt),
    // Meeting transcripts are written line by line and never counted, so 0 means unknown here.
    transcript && transcript.wordCount > 0 ? `${transcript.wordCount.toLocaleString()} words` : null,
  ].filter(Boolean);

  return (
    <div className="page transcript-page">
      <header className="page-head tx-head">
        <div className="tx-head-main">
          <div className="crumbs small">
            <Link to={`/p/${recording.projectId}/recordings`}>Recordings</Link>
          </div>
          <h1>{recording.title}</h1>
          <p className="muted small">
            {meta.join(' · ')}
            {recording.status !== 'ready' ? (
              <>
                {' '}
                <Badge tone={recording.status === 'failed' ? 'bad' : 'warn'}>{recording.status}</Badge>
              </>
            ) : null}
          </p>
        </div>
        <button type="button" className="btn btn-primary" onClick={() => void review()} disabled={!transcript || busy}>
          {reviewId ? 'Open the review' : 'Review with Jabberr'}
        </button>
      </header>
      {error ? <ErrorBox>{error}</ErrorBox> : null}
      {recording.status === 'failed' ? <ErrorBox>Transcription failed: {recording.error ?? 'unknown error'}</ErrorBox> : null}

      {!transcript ? (
        <div className="card">
          <Spinner
            label={
              recording.status === 'queued'
                ? 'Queued: waiting for a transcription worker'
                : recording.status === 'transcribing'
                  ? 'Transcribing'
                  : recording.status === 'uploaded'
                    ? 'Upload not completed'
                    : 'No transcript'
            }
          />
        </div>
      ) : (
        <>
          <section className="tx-people" aria-label="People in this recording">
            <ul className="tx-people-list">
              {speakers.map(({ id, share }) => {
                const name = nameOf(id);
                return (
                  <li key={id} className="tx-person">
                    <Avatar name={name} id={isBot(id) ? BOT_IDENTITY : name} size={32} bot={isBot(id)} avatarId={assistantAvatar} />
                    {editing === id ? (
                      <input
                        className="tx-person-input"
                        autoFocus
                        value={draftName}
                        onChange={(e) => setDraftName(e.target.value)}
                        onBlur={() => void rename(id, draftName)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') void rename(id, draftName);
                          if (e.key === 'Escape') setEditing(null);
                        }}
                        aria-label={`Name for ${name}`}
                        placeholder={id}
                      />
                    ) : (
                      <span className="tx-person-name">
                        <strong>{name}</strong>
                        <span className="muted tiny">{share}% of the talking</span>
                      </span>
                    )}
                    <span className="tx-person-actions">
                      {!isBot(id) && editing !== id ? (
                        <button
                          type="button"
                          className="link-btn tiny"
                          onClick={() => {
                            setDraftName(transcript.speakerMap[id] ?? '');
                            setEditing(id);
                          }}
                        >
                          Rename
                        </button>
                      ) : null}
                      {!isBot(id) ? (
                        <button
                          type="button"
                          className="link-btn tiny"
                          disabled={!reviewId || drafting !== null}
                          title={reviewId ? `Draft a stakeholder readout for ${name} on the review thread` : 'Start the review first'}
                          onClick={() => void sendOnReview(`readout:${id}`, 'readout', `Stakeholder: ${name}`)}
                        >
                          {drafting === `readout:${id}` ? 'Drafting…' : 'Readout'}
                        </button>
                      ) : null}
                    </span>
                  </li>
                );
              })}
            </ul>
            {!reviewId ? <p className="muted tiny">Start the review to draft readouts and requirements from this transcript.</p> : null}
          </section>

          <ol className="tx-lines">
            <TranscriptGroups segments={transcript.segments}>{(s, i) => {
              const continued = i > 0 && transcript.segments[i - 1]?.speaker === s.speaker;
              const name = nameOf(s.speaker);
              return (
                <li key={i} className={`tx-line ${continued ? 'tx-line-continued' : ''} ${isBot(s.speaker) ? 'tx-line-bot' : ''}`}>
                  <span className="tx-line-avatar">
                    {continued ? null : <Avatar name={name} id={isBot(s.speaker) ? BOT_IDENTITY : name} size={32} bot={isBot(s.speaker)} avatarId={assistantAvatar} />}
                  </span>
                  <div className="tx-line-body">
                    {continued ? null : (
                      <div className="tx-line-head">
                        <span className="tx-line-name">{name}</span>
                        <span className="tx-line-time mono tiny">{mmss(s.start)}</span>
                      </div>
                    )}
                    <p className="tx-line-text">{s.text}</p>
                  </div>
                  {reviewId ? (
                    <button
                      type="button"
                      className="tx-line-action"
                      disabled={drafting !== null}
                      title="Quote this line into a requirements draft on the review thread"
                      onClick={() => void sendOnReview(`line:${i}`, 'requirements', s.text)}
                    >
                      {drafting === `line:${i}` ? 'Drafting…' : 'Draft requirements'}
                    </button>
                  ) : null}
                </li>
              );
            }}</TranscriptGroups>
          </ol>
        </>
      )}
    </div>
  );
}

const SOURCE_LABEL: Record<string, string> = {
  meeting: 'Meeting',
  upload: 'Uploaded audio',
  browser: 'Recorded in the browser',
  vtt: 'Imported transcript',
};
